
import { Vue, Component, Watch, Prop } from "vue-property-decorator";
import catalogService from "@/modules/modal-planning/services/catalog.service";
import VueElementLoading from 'vue-element-loading';
import productService from "@/modules/modal-planning/services/product.service";
import {
    analysis,
    SaveOptions,
    analyzeAndSave,
    saveAnalysis,
    AnalysisResponse,
    SaveAnalysisResponse
} from "@/modules/modal-planning/services/bdc_client"
import {
    saveProductSelection,
    OktruProductSelection,
    OktruCategoryAttribute,
    SaveProductSelectionResult
} from "@/modules/modal-planning/services/oktru-save.service"

/**
 * Характеристика товара в консолидированном ответе виджета:
 * одна запись attributes_dict -> один элемент массива product_attributes.
 */
interface ProductAttributeItem {
    key: string;
    name_ru: string;
    name_kz: string;
    value_ru: string;
    value_kz: string;
    value_code: string;
}

/**
 * Консолидированный ответ виджета ОКТРУ - то, что уходит наружу по кнопке "ВЫБРАТЬ".
 * Собирается из трёх источников: карточка товара, analysis и save_analysis (БДЦ).
 * Имена полей зафиксированы постановкой (в т.ч. name_kz/value_kz, а не kk) - не переименовывать.
 */
interface OktruWidgetResponse {
    id_prod: number | null;
    oktru: string | null;
    oktru_name: string | null;
    koh: string | null;
    inflation_price: number | null;
    median_price: number | null;
    bdc_file_url: string | null;
    bdc_file_id: string | null;
    status: number | null;
    product_attributes: ProductAttributeItem[];
}

/** Причина завершения работы виджета без выбора товара (событие widget-closed). */
type WidgetCloseReason = "cancelled" | "product_not_found" | "catalog_unavailable";

interface ProductAttribute {
    name: {
        kz: string;
        ru: string;
    };
    value: {
        kz: string;
        ru: string;
    };
    is_main: boolean;
    value_code: string;
    is_tech_spec: boolean;
    is_required_egz: boolean;
    is_required_kgd: boolean;
}

interface Category {
    id: number;
    name: string;
    code: string | null;
    full_code: string;
    level: number;
    parent: number;
    category_type: number;
    image: string | null;
    ancestors?: Category[];
    full_path?: string;
}

interface ProductImage {
    id: number;
    order: number;
    image_url: string;
    hidden: boolean;
}

interface EnstruEntry {
    id: number;
    enstru: {
        id: number;
        name: string;
        code: string;
        short_description: string;
    };
    timestamp: number;
}


interface Product {
    id: number;
    external_id: string | null;
    name: string;
    is_domestic: boolean;
    is_social: boolean;
    product_type: number;
    model_name: string | null;
    gtin: string | null;
    mpn: string | null;
    kztin_code: string;
    net_weight: number | null;
    net_measure: string | null;
    gross_weight: number | null;
    gross_measure: string | null;
    volume: number | null;
    standards: string | null;
    volume_measure: string | null;
    manufacturer: string | null;
    amount_in_package: number | null;
    package_dimensions: string | null;
    product_dimensions: string | null;
    hierarchy: number;
    source: number;
    status: number;
    created: number;
    modified: number;
    oked: string | null;
    category: Category;
    tnved: string | null;
    tnveds: any[];
    brand: string | null;
    measure: string | null;
    country: string | null;
    child_product: any | null;
    attributes_dict: Record<string, ProductAttribute>;
    enstrus: EnstruEntry[];
    images: ProductImage[];
    documents: any[];
    active_product_kztin: string | null;
    deactivation_reason: string | null;
    active_product_id: number | null;
    manufacturer_bin: string | null;
    ntin_code: string;
    ntin_isdeactivated: boolean;
    ntin_deactivationreason: string | null;
    ntin_duplicateofproduct: string | null;
    ntin_isdeactivated_at: string | null;
    ntin_active_product_ids: number[];
}

interface AttrItem {
    key: string;
    value: string;
}

interface ResultFormat {
    category_id: number;
    attrs: AttrItem[];
}

interface CategoryAttributeValue {
    code: string;
    value: {
        kz: string;
        ru: string;
    };
}

interface CategoryAttributeGroup {
    code: string;
    name: {
        kz: string;
        ru: string;
    };
    values: CategoryAttributeValue[];
}

@Component({
    components: { 'loading': VueElementLoading }
})
export default class ModalOkrtuForm extends Vue {
    /** Идентификатор товара НКТ: если задан - виджет открывается сразу на карточке, без каталога */
    @Prop({ type: Number, default: null }) readonly productId!: number | null;
    /** Код ОКТРУ вызывающей стороны для сверки с category.full_code товара */
    @Prop({ type: String, default: null }) readonly oktruCode!: string | null;

    /** Товар выбран - закрытие карточки не считается отменой */
    selectionEmitted = false;

    /** КАТО региона госоргана, ведущего бюджетное планирование - передаётся вызывающей стороной через query */
    gu_kato: string | null = null;

    get isDirectMode(): boolean {
        return this.productId != null;
    }

    bredcrumb = [
        {
            text: "Admin",
            href: "#",
        },
        {
            text: "Manage",
            href: "#",
        },
        {
            text: "Library",
            active: true,
        },
    ];
    currentPage = 1;
    perPage = 12;
    totalRows = 47;
    dropdownList = [];
    searchQuery = "";
    categories: any[] = [];
    categoryIds: string[] = [];
    category_id: string[] = [];
    categoriesByProducts: any = [];
    productsByName: any = [];
    selectedSuggestion: Category | null = null;
    suggestDebounceId: number | null = null;
    selectedCategoryPath: Category[] = [];
    currentLevelCategories: Category[] = [];
    categoryAttributes: CategoryAttributeGroup[] = [];
    selectedAttrValues: Record<string, string[]> = {};
    expandedAttrGroups: Record<string, boolean> = {};
    maxVisibleAttrValues = 5;
    productTypeLevel = 3;
    dataSource: any[] = [];
    productsNextUrl: string | null = null;
    filters: any = {
        q: "",
        category_id: null,
        dict_attrs: [],
        is_domestic: null,
        // only_category_active: true,
        // only_with_attributes: true,
    };
    is_domestics = false;
    is_social = false;
    activeTab = 0;
    mytabs = ["Основная информация", "Характеристики", "Дополнительно"];

    @Watch("is_domestics")
    change_is_domestics(val: boolean, old_val: boolean) {
        this.filters.is_domestic = val;
        this.push_route();
        this.loadData();
    }

    @Watch("is_social")
    change_is_social(val: boolean, old_val: boolean) {
        this.filters.is_social = val;
        this.push_route();
        this.loadData();
    }

    @Watch("currentPage")
    change_page(val: number, old_val: number) {
        this.getProducts();
    }

    // @Watch('category_id')
    change_categiry_id(val: string[], old_val: string[]) {
        this.filters.category_id = val.join(",");
        this.push_route();
        this.loadData();
    }

    get showCategoryAttributes(): boolean {
        const last = this.selectedCategoryPath[this.selectedCategoryPath.length - 1];
        return !!last && last.level >= this.productTypeLevel;
    }

    closeDropdown() {
        this.showDropdown = false;
        this.categoriesLoaded = false;
        this.categories = [];
        this.categoriesByProducts = [];
    }

    push_route() {
        // this.$router.push({
        //     path: "/modal-planning",
        //     query: {
        //         q: this.searchQuery,
        //         is_domestics: this.is_domestics.toString(),
        //         is_social: this.is_social.toString(),
        //         category_id: this.category_id.join(","),
        //     },
        // });
    }

    searchProducts() {
        this.closeDropdown();
        this.filters.q = this.searchQuery;
        // this.$router.push({
        //     path: '/modal-planning-new',
        //     query: {q: this.searchQuery}
        // });
        // this.push_route();
        this.loadData();
    }

    onSuggestionInput(query: string) {
        this.searchQuery = query || "";

        if (this.suggestDebounceId !== null) {
            window.clearTimeout(this.suggestDebounceId);
        }

        this.suggestDebounceId = window.setTimeout(() => {
            this.loadSearchSuggestions();
        }, 250);
    }

    onSuggestionSelect(option: Category | null) {
        if (!option) return;

        this.selectedSuggestion = option;
        this.searchQuery = this.getSuggestionLabel(option);

        const ancestors = Array.isArray(option.ancestors)
            ? option.ancestors.slice().sort((a, b) => a.level - b.level)
            : [];

        this.selectedCategoryPath = [...ancestors, option];
        this.resetAttributeFilters();
        this.currentPage = 1;
        this.applyCategoryFilter();
        this.loadPanelForSelectedCategory(option);
        this.getProducts();
    }

    async initCategoryNavigation() {
        try {
            this.currentLevelCategories = await catalogService.getRootCategories();
        } catch (error) {
            console.error("Error loading root categories:", error);
            this.currentLevelCategories = [];
        }
    }

    resetAttributeFilters() {
        this.selectedAttrValues = {};
        this.expandedAttrGroups = {};
        this.categoryAttributes = [];
        this.filters.dict_attrs = [];
    }

    async selectCategory(category: Category) {
        this.selectedCategoryPath.push(category);
        this.resetAttributeFilters();
        this.currentPage = 1;
        this.applyCategoryFilter();
        await this.loadPanelForSelectedCategory(category);
        await this.getProducts();
    }

    async navigateToCategoryPathIndex(index: number) {
        if (index < 0) {
            this.selectedCategoryPath = [];
        } else {
            this.selectedCategoryPath = this.selectedCategoryPath.slice(
                0,
                index + 1
            );
        }

        this.resetAttributeFilters();
        this.currentPage = 1;
        this.applyCategoryFilter();

        const last = this.selectedCategoryPath[this.selectedCategoryPath.length - 1];
        if (last) {
            await this.loadPanelForSelectedCategory(last);
        } else {
            await this.initCategoryNavigation();
        }

        await this.getProducts();
    }

    async loadPanelForSelectedCategory(category: Category) {
        if (category.level >= this.productTypeLevel) {
            this.currentLevelCategories = [];
            await this.loadCategoryAttributes(category.id);
            return;
        }

        try {
            this.currentLevelCategories = await catalogService.getChildCategories(
                category.id
            );
        } catch (error) {
            console.error("Error loading child categories:", error);
            this.currentLevelCategories = [];
        }
    }

    async loadCategoryAttributes(categoryId: number) {
        try {
            const response = await catalogService.getCategoryAttrs(categoryId);
            const attrs = Array.isArray(response?.attributes_dict)
                ? response.attributes_dict
                : [];

            this.categoryAttributes = attrs;
            this.selectedAttrValues = attrs.reduce(
                (acc: Record<string, string[]>, attr: CategoryAttributeGroup) => {
                    acc[attr.code] = [];
                    return acc;
                },
                {}
            );
        } catch (error) {
            console.error("Error loading category attributes:", error);
            this.categoryAttributes = [];
            this.selectedAttrValues = {};
        }
    }

    applyCategoryFilter() {
        const last =
            this.selectedCategoryPath[this.selectedCategoryPath.length - 1];

        if (last) {
            this.category_id = [String(last.id)];
            this.filters.category_id = String(last.id);
        } else {
            this.category_id = [];
            this.filters.category_id = null;
        }

        this.push_route();
    }

    onAttributeFilterChange() {
        this.filters.dict_attrs = Object.entries(this.selectedAttrValues).flatMap(
            ([key, values]) =>
                values.map((value) => ({
                    key,
                    value,
                }))
        );
        this.currentPage = 1;
        this.push_route();
        this.getProducts();
    }

    toggleAttrGroupExpanded(code: string) {
        this.$set(
            this.expandedAttrGroups,
            code,
            !this.expandedAttrGroups[code]
        );
    }

    getVisibleAttrValues(attr: CategoryAttributeGroup): CategoryAttributeValue[] {
        if (this.expandedAttrGroups[attr.code]) {
            return attr.values;
        }
        return attr.values.slice(0, this.maxVisibleAttrValues);
    }

    getAttrCheckboxOptions(attr: CategoryAttributeGroup) {
        return this.getVisibleAttrValues(attr).map((item) => ({
            value: item.code,
            text: item.value.ru,
        }));
    }

    showDropdown = false;
    categoriesLoaded = false;

    async loadSearchSuggestions() {
        try {
            if (!this.searchQuery?.trim()) {
                this.categoriesByProducts = [];
                this.productsByName = [];
                return;
            }

            this.isLoading = true;
            this.showDropdown = true;
            this.categoriesLoaded = false;

            const categoryIdsStr = "";
            const catsByProducts = await catalogService.searchCategoriesByProductName(
                this.searchQuery,
                categoryIdsStr
            );

            this.categoriesByProducts = catsByProducts || [];
            this.productsByName = (this.categoriesByProducts || []).map(
                (item: Category) => ({
                    ...item,
                    full_path: this.buildCategoryPath(item),
                })
            );
            this.categoriesLoaded = true;
            this.isLoading = false;
        } catch (error) {
            console.error("Search error:", error);
            this.isLoading = false;
        }
    }

    buildCategoryPath(option: Category): string {
        if (!option) return "";
        if (option.full_path) return option.full_path;

        const ancestors = Array.isArray(option.ancestors) ? option.ancestors : [];
        const parts = ancestors.map((ancestor: Category) => ancestor.name).filter(Boolean);

        if (option.name) parts.push(option.name);
        return parts.join(" / ");
    }

    getSuggestionLabel(option: any): string {
        if (typeof option === "string") return option;
        if (!option || typeof option !== "object") return "";
        return option.full_path || this.buildCategoryPath(option) || option.name || "";
    }

    escapeRegExp(value: string): string {
        return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    }

    escapeHtml(value: string): string {
        return value
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    }

    highlightSuggestion(option: any): string {
        const label = this.getSuggestionLabel(option);
        const query = this.searchQuery?.trim();

        if (!label) return "";
        if (!query) return this.escapeHtml(label);

        const splitRegex = new RegExp(`(${this.escapeRegExp(query)})`, "ig");
        return label
            .split(splitRegex)
            .map((part) =>
                part.toLowerCase() === query.toLowerCase()
                    ? `<strong>${this.escapeHtml(part)}</strong>`
                    : this.escapeHtml(part)
            )
            .join("");
    }

    async loadData() {
        this.inflationPrice = 0;
        await this.loadSearchSuggestions();
        await this.getProducts();
    }

    async getProducts() {
        try {
            // this.isLoaded = false;
            // this.attrsLoaded = false;

            this.isLoading = true;
            const response = await productService.getProducts(
                this.filters,
                this.perPage,
                (this.currentPage - 1) * this.perPage
            );

            this.dataSource = response.result || [];
            this.productsNextUrl = response.next;
            this.totalRows = response.count;
            // this.isLoaded = true;
            // this.attrsLoaded = true;
            this.isLoading = false;
        } catch (error) {
            console.error("Error loading products:", error);
            this.isLoading = false;
            // this.isLoaded = true;
            // this.attrsLoaded = true;
        }
    }

    async mounted() {
        if (this.isDirectMode) {
            await this.openProductDirectly(this.productId as number);
            return;
        }
        this.initFromRoute();
        await this.initCategoryNavigation();
        await this.restoreCategoryPathFromRoute();
        this.loadData();
    }

    async openProductDirectly(id: number) {
        try {
            await this.show_product(id);
        } catch (error) {
            const status = (error as { response?: { status?: number } })?.response?.status;
            this.closeWidget(status === 404 ? "product_not_found" : "catalog_unavailable");
            return;
        }

        // товар мог быть переклассифицирован в НКТ: карточка и расчёт идут по фактической
        // категории, её код и наименование уходят в ответе - вызывающая сторона сверит сама
        const productCode = this.selected_product?.category?.full_code || null;
        if (this.oktruCode && productCode !== this.oktruCode) {
            console.warn(
                `ОКТРУ товара ${id} в НКТ (${productCode}) не совпадает с переданным (${this.oktruCode})`
            );
        }
    }

    closeWidget(reason: WidgetCloseReason) {
        this.$emit("widget-closed", { reason });
    }

    onProductModalHidden() {
        const cancelled = this.isDirectMode && !this.selectionEmitted;
        this.resetPrice();
        if (cancelled) {
            this.closeWidget("cancelled");
        }
    }

    async restoreCategoryPathFromRoute() {
        const query = this.$route.query;
        const categoryIdRaw = query.category_id || query.category;

        if (!categoryIdRaw) return;

        const categoryId = parseInt(String(categoryIdRaw).split(",")[0], 10);
        if (Number.isNaN(categoryId)) return;

        try {
            const ancestors = await catalogService.getCategoryAncestors(categoryId);
            const sortedAncestors = (ancestors || []).slice().sort(
                (a: Category, b: Category) => a.level - b.level
            );

            let currentCategory: Category | null = null;
            if (sortedAncestors.length > 0) {
                const children = await catalogService.getChildCategories(
                    sortedAncestors[sortedAncestors.length - 1].id
                );
                currentCategory =
                    children.find((item: Category) => item.id === categoryId) ||
                    null;
            } else {
                const roots = await catalogService.getRootCategories();
                currentCategory =
                    roots.find((item: Category) => item.id === categoryId) || null;
            }

            if (!currentCategory) {
                currentCategory = {
                    id: categoryId,
                    name: "",
                    code: null,
                    full_code: "",
                    level: sortedAncestors.length,
                    parent: sortedAncestors[sortedAncestors.length - 1]?.id || 0,
                    category_type: 1,
                    image: null,
                };
            }

            this.selectedCategoryPath = [...sortedAncestors, currentCategory];
            await this.loadPanelForSelectedCategory(currentCategory);
        } catch (error) {
            console.error("Error restoring category path:", error);
        }
    }

    // Methods
    initFromRoute() {
        const query = this.$route.query;
        //
        // if (query.source) this.source = query.source as string;
        // if (query.bzFormId) this.bzFormId = query.bzFormId as string;
        // if (query.factId) this.factId = query.factId as string;
        // if (query.iin) this.iin = query.iin as string;
        // if (query.gu_code) this.gu_code = query.gu_code as string;
        if (query.gu_kato) this.gu_kato = query.gu_kato as string;
        // if (query.email) this.email = query.email as string;

        if (query.q) {
            this.searchQuery = query.q as string;
            this.filters.q = query.q;
        }

        if (query.category_ids) {
            const categoryIdsStr = query.category_ids as string;
            // this.categoryIds = categoryIdsStr.split(',');
            this.filters.category_level0_ids = categoryIdsStr;
        }

        if (query.category_id) {
            this.category_id = query.category_id.toString().split(",");
            this.filters.category_id = query.category_id;
        }
        if (query.category) {
            this.filters.category_id = parseInt(query.category as string);
            // this.setAttributesByCategory(this.filters.category_id);
        }

        // Collect attributes from query params
        // this.filters.dict_attrs = this.collectAttrsFromQuery(query);
    }

    selected_product: Product | null = null;
    /** Консолидированный ответ виджета, который уходит наружу по кнопке "ВЫБРАТЬ" */
    call_back_object: OktruWidgetResponse | null = null;
    selected_categories: Category[] = [];
    modalCategoryAttributes: CategoryAttributeGroup[] = [];
    modalSelectedAttrValues: Record<string, string[]> = {};
    modalExpandedAttrGroups: Record<string, boolean> = {};
    modalAttrs: AttrItem[] = [];

    async show_product(id: number) {
        this.selected_product = (await productService.getProduct(
            id
        )) as Product;
        this.selected_categories = await catalogService.getCategoryAncestors(
            this.selected_product.category.id
        );
        this.resetPrice();
        await this.loadModalCategoryAttributes(this.selected_product.category.id);
        this.$bvModal.show("prod_modal");
    }

    async loadModalCategoryAttributes(categoryId: number) {
        try {
            const response = await catalogService.getCategoryAttrs(categoryId);
            const attrs = Array.isArray(response?.attributes_dict)
                ? response.attributes_dict
                : [];

            this.modalCategoryAttributes = attrs;
            this.modalSelectedAttrValues = attrs.reduce(
                (acc: Record<string, string[]>, attr: CategoryAttributeGroup) => {
                    acc[attr.code] = [];
                    return acc;
                },
                {}
            );
            this.modalExpandedAttrGroups = {};
        } catch (error) {
            console.error("Error loading modal category attributes:", error);
            this.modalCategoryAttributes = [];
            this.modalSelectedAttrValues = {};
        }
    }

    onModalAttributeChange() {
        this.modalAttrs = Object.entries(this.modalSelectedAttrValues).flatMap(
            ([key, values]) =>
                values.map((value) => ({
                    key,
                    value,
                }))
        );
        // акт обосновывает цену по конкретному набору характеристик -
        // после их смены прежний расчёт недействителен
        this.resetCalculationResult();
    }

    get hasProductAttrs(): boolean {
        return Object.keys(this.selected_product?.attributes_dict || {}).length > 0;
    }

    get hasCategoryAttrs(): boolean {
        return this.modalCategoryAttributes.length > 0;
    }

    get hasCheckedAttrs(): boolean {
        return Object.values(this.modalSelectedAttrValues).some(
            (values) => values.length > 0
        );
    }

    get canCalculate(): boolean {
        return this.hasProductAttrs || this.hasCategoryAttrs;
    }

    toggleModalAttrGroupExpanded(code: string) {
        this.$set(
            this.modalExpandedAttrGroups,
            code,
            !this.modalExpandedAttrGroups[code]
        );
    }

    getModalVisibleAttrValues(attr: CategoryAttributeGroup): CategoryAttributeValue[] {
        if (this.modalExpandedAttrGroups[attr.code]) {
            return attr.values;
        }
        return attr.values.slice(0, this.maxVisibleAttrValues);
    }

    getModalAttrCheckboxOptions(attr: CategoryAttributeGroup) {
        return this.getModalVisibleAttrValues(attr).map((item) => ({
            value: item.value.ru,
            text: item.value.ru,
        }));
    }

    showMessage(tostbody: string, title: boolean) {
        this.$bvToast.toast(tostbody, {
            title: "Сообщение",
            variant: "info",
            autoHideDelay: 4000,
            solid: true,
        });
    }

    // convertAttrToArray() {
    //
    // }

    convertAttributesToArray(attributesDict: any) {
        return Object.keys(attributesDict).map((key) => ({
            key: key,
            value: attributesDict[key].value.ru,
        }));
    }
    medianPrice = 0;
    medianPriceNew = 0;
    inflationPrice = 0;
    certificateType = 0;
    pdf_akt_url = "";
    koh_code = "";

    resetCalculationResult() {
        this.medianPrice = 0;
        this.medianPriceNew = 0;
        this.pdf_akt_url = "";
        this.koh_code = "";
        this.inflationPrice = 0;
        this.certificateType = 0;
        this.call_back_object = null;
        this.is_product_ready = false;
    }

    resetPrice() {
        this.resetCalculationResult();
        this.modalAttrs = [];
        this.modalSelectedAttrValues = Object.keys(
            this.modalSelectedAttrValues
        ).reduce((acc: Record<string, string[]>, code) => {
            acc[code] = [];
            return acc;
        }, {});
        this.modalExpandedAttrGroups = {};
    }

    isLoading = false;
    is_product_ready = false;

    /** Идёт сохранение выбранной позиции ОКТРУ в нашу БД */
    isSavingOktru = false;

    async selectProduct() {
        if (this.selected_product == null) {
            return;
        }
        if (this.is_product_ready) {
            await this.emitSelection(this.call_back_object);
            return;
        }

        if (!this.canCalculate) {
            const confirmed = await this.confirmDialog(
                "Для категории этого товара нет характеристик, по которым можно выполнить расчёт цены в БДЦ. " +
                    "Товар можно добавить без расчёта: цену нужно будет указать вручную, акт по строке формироваться не будет.",
                "Добавить без расчёта",
                "Отмена"
            );
            if (confirmed) {
                await this.emitSelection(this.buildWidgetResponseWithoutCalculation(this.selected_product));
            }
            return;
        }

        if (!this.hasProductAttrs && !this.hasCheckedAttrs) {
            const confirmed = await this.confirmDialog(
                "Вы не выбрали характеристики товара, поэтому расчёт цены в БДЦ не выполнялся. " +
                    "Отметьте характеристики на вкладке «Характеристики» и нажмите «Расчет» — тогда в расшифровку попадут цена по БДЦ и/или акт. " +
                    "Если продолжить без расчёта, цену нужно будет ввести вручную, а акт добавлен не будет.",
                "Продолжить без расчёта",
                "Выбрать характеристики"
            );
            if (confirmed) {
                await this.emitSelection(this.buildWidgetResponseWithoutCalculation(this.selected_product));
            } else {
                this.activeTab = 1;
            }
            return;
        }

        const confirmed = await this.confirmDialog(
            "Вы не выполнили расчёт цены по товару в БДЦ. " +
                "Рекомендуем сначала нажать «Расчет» — тогда в расшифровку попадут цена по БДЦ и/или акт. " +
                "Если продолжить без расчёта, цену нужно будет ввести вручную, а акт добавлен не будет.",
            "Продолжить без расчёта",
            "Выполнить расчёт"
        );
        if (confirmed) {
            await this.emitSelection(this.buildWidgetResponseWithoutCalculation(this.selected_product));
        }
    }

    async emitSelection(response: OktruWidgetResponse | null) {
        this.selectionEmitted = true;
        this.$emit("product-selected", response);
        this.$bvModal.hide("prod_modal");
        await this.saveOktruSelection();
    }

    confirmDialog(message: string, okTitle: string, cancelTitle: string): Promise<boolean> {
        return this.$bvModal
            .msgBoxConfirm(message, {
                title: "Расчёт цены не выполнен",
                okTitle,
                cancelTitle,
                okVariant: "primary",
                cancelVariant: "outline-secondary",
                centered: true,
                noCloseOnBackdrop: true,
            })
            .then((value) => value === true);
    }

    buildWidgetResponseWithoutCalculation(product: Product): OktruWidgetResponse {
        return {
            id_prod: product.id ?? null,
            oktru: product.category?.full_code || null,
            oktru_name: product.category?.name || null,
            koh: null,
            inflation_price: null,
            median_price: null,
            bdc_file_url: null,
            bdc_file_id: null,
            status: null,
            product_attributes: this.buildProductAttributes(product.attributes_dict || null),
        };
    }

    /**
     * Извлекает идентификатор PDF-акта БДЦ (guid) из ссылки на файл.
     * Ссылка имеет вид .../bdc-doc/download?guid=<guid>; если параметра нет - вернёт null.
     */
    extractBdcFileId(bdcFileUrl: string | null): string | null {
        if (!bdcFileUrl) {
            return null;
        }
        const match = /[?&]guid=([^&#]+)/.exec(bdcFileUrl);
        return match ? decodeURIComponent(match[1]) : null;
    }

    /**
     * Преобразует attributes_dict карточки товара в массив product_attributes
     * (ключ записи становится полем key).
     */
    buildProductAttributes(attributesDict: Record<string, ProductAttribute> | null): ProductAttributeItem[] {
        const dict = attributesDict || {};
        return Object.keys(dict).map((key) => {
            const attr = dict[key];
            return {
                key,
                name_ru: attr?.name?.ru || "",
                name_kz: attr?.name?.kz || "",
                value_ru: attr?.value?.ru || "",
                value_kz: attr?.value?.kz || "",
                value_code: attr?.value_code || "",
            };
        });
    }

    /**
     * Собирает единый ответ виджета из трёх источников:
     *  - карточка товара (id, категория, характеристики);
     *  - analysis (статус и цены);
     *  - save_analysis (КОХ и ссылка на PDF-акт БДЦ).
     * Служебные поля товара и статус-коды подписания (sign_data/sma_doc/sma_result/isgp)
     * в ответ намеренно не попадают.
     */
    buildWidgetResponse(
        product: Product,
        analysisResult: AnalysisResponse,
        saveResult: SaveAnalysisResponse
    ): OktruWidgetResponse {
        const bdcFileUrl = saveResult?.pdf || null;
        const content = analysisResult?.content;

        return {
            id_prod: product?.id ?? null,
            oktru: product?.category?.full_code || null,
            oktru_name: product?.category?.name || null,
            koh: saveResult?.koh || null,
            inflation_price: content?.inflation_price ?? null,
            median_price: content?.median_price ?? null,
            bdc_file_url: bdcFileUrl,
            bdc_file_id: this.extractBdcFileId(bdcFileUrl),
            status: analysisResult?.status ?? null,
            product_attributes: this.buildProductAttributes(product?.attributes_dict || null),
        };
    }

    /**
     * Сохранение выбранной категории ОКТРУ, её характеристик, значений и связей
     * с ЕНСТРУ в нашу БД (sit-py, /api-py/oktru/*).
     * Вызывается из самой модалки, чтобы работать на любой странице,
     * где она используется, а не только там, где слушают product-selected.
     */
    async saveOktruSelection() {
        // сохраняем именно сырые данные товара (категория/характеристики/ЕНСТРУ),
        // а не консолидированный ответ виджета - в нём этих полей уже нет
        if (this.selected_product == null) {
            return;
        }

        this.isSavingOktru = true;
        try {
            const result: SaveProductSelectionResult = await saveProductSelection(
                this.selected_product as unknown as OktruProductSelection,
                // характеристики категории со всеми допустимыми значениями - содержимое справочника
                (this.modalCategoryAttributes || []) as OktruCategoryAttribute[]
            );
            this.$emit("oktru-saved", result);

            if (result.errors.length === 0) {
                this.$bvToast.toast(
                    this.$t('modules.oktru_example.save_success', {
                        categorySaved: result.categorySaved
                            ? this.$t('modules.oktru_example.yes')
                            : this.$t('modules.oktru_example.no'),
                        attributesSaved: result.attributesSaved,
                        valuesSaved: result.valuesSaved,
                        enstruLinksSaved: result.enstruLinksSaved,
                    }).toString(),
                    {
                        title: this.$t('modules.common.data_saved_successfully').toString(),
                        variant: 'success',
                        autoHideDelay: 6000,
                        solid: true,
                    }
                );
            } else {
                this.$bvToast.toast(
                    this.$t('modules.oktru_example.save_partial', {
                        errorsCount: result.errors.length,
                    }).toString(),
                    {
                        title: this.$t('modules.common.warning').toString(),
                        variant: 'warning',
                        autoHideDelay: 8000,
                        solid: true,
                    }
                );
            }
        } catch (e) {
            this.$emit("oktru-save-error", e);
            this.$bvToast.toast(String(e), {
                title: this.$t('modules.oktru_example.save_error').toString(),
                variant: 'danger',
                autoHideDelay: 8000,
                solid: true,
            });
        } finally {
            this.isSavingOktru = false;
        }
    }

    async generatePrice() {
        if (
            this.selected_product != null &&
            Object.keys(this.selected_product.attributes_dict || {}).length > 0
        ) {
            this.modalAttrs = this.convertAttributesToArray(
                this.selected_product.attributes_dict
            );
        } else {
            this.onModalAttributeChange();
        }
        if (!this.selected_product?.category) {
            this.showMessage("Не указан код продукта", true);
            return;
        }
        if (this.selected_product.category.level !== 3) {
            this.showMessage("Необходимо выбрать тип", true);
            return;
        }
        if (this.modalAttrs.length === 0) {
            this.showMessage("Выберите атрибуты или продукт", true);
            return;
        }

        // товар здесь уже проверен выше - фиксируем его для сборки ответа виджета
        const product: Product = this.selected_product;

        try {
            this.isLoading = true;

            const data = {
                category_id: this.selected_product.category.id,
                attrs: this.modalAttrs.slice(),
            };

            // const response = await productService.getMedianPrice(data);

            // const analysisResult = await analysis(data);
            if (!this.gu_kato) {
                console.warn("КАТО госоргана не передан (query gu_kato) - медианная цена БДЦ будет рассчитана без учёта региона");
            }
            const save_opts: SaveOptions = {
                kato: this.gu_kato || "000000000",
                iin: "000000000000",
                url: "https://example.com",
                source_system_id: 3,
                certificateType: 1
            }

            const analyze_result = await analyzeAndSave(this.selected_product, data, save_opts);
            this.medianPriceNew = analyze_result.analysisResult.content.median_price;
            this.medianPrice = analyze_result.analysisResult.content.median_price;
            this.inflationPrice = analyze_result.analysisResult.content.inflation_price;

            // const save_result = await analyzeAndSave(this.selected_product, save_opts);
            console.debug("PDF акт:", analyze_result.saveResult.pdf);
            console.debug("КОХ-код:", analyze_result.saveResult.koh);
            this.pdf_akt_url = analyze_result.saveResult.pdf;
            this.koh_code = analyze_result.saveResult.koh;
            // Единый (консолидированный) ответ виджета: только поля из целевого списка,
            // без вложенности analysisResult/saveResult и без служебных полей товара.
            // Сырые данные товара для сохранения в наши таблицы ОКТРУ берутся
            // из selected_product (см. saveOktruSelection), а не из этого ответа.
            this.call_back_object = this.buildWidgetResponse(
                product,
                analyze_result.analysisResult,
                analyze_result.saveResult
            );
            this.is_product_ready = true;

            // const attributesForCertificate = this.certificateAttributes;
            // const date = this.getDate();
            // const time = this.getTime();

            // const tempData = {
            //     date,
            //     time,
            //     kato: this.gu_kato,
            //     oktru_code: this.category.full_code,
            //     category_name: this.category.name,
            //     // attributes: attributesForCertificate,
            //     certificateType: response.status
            // };

            if (analyze_result.analysisResult.status === 1) {
                // this.certificateType = 1;
                console.debug(analyze_result.analysisResult.content);
                // this.medianPrice = analysisResult.content.median_price;
                // this.inflationPrice = analysisResult.content.inflation_price;
                //     this.certificateData = {...tempData, ...response.content};
            } else if (analyze_result.analysisResult.status === 2) {
                //     this.certificateType = 2;
                //     this.certificateData = {...tempData, ...response.content};
                this.showMessage("Отсутствуют цены по товарам", true);
            } else if (analyze_result.analysisResult.status === 3) {
                //     this.certificateType = 3;
                //     this.certificateData = {...tempData, ...response.content};
                this.showMessage("Товары не найдены", true);
            }

            // this.priceIsCalculated = true;
            this.isLoading = false;
        } catch (error) {
            console.error("Error calculating price:", error);
            this.isLoading = false;
            this.showMessage("Ошибка вывода медианной цены", true);
        }
    }
}
